import { type AppIcons } from '@backrs_ui/common-ui';

import courseSelectionBanner from '@backrs_ui/grow-ui/src/assets/images/course-selection-guide/course-selection-banner.png';
import {
  GuideFaqSection,
  GuideMythsAndMistakesSection,
  GuidePageShell,
  GuideSections,
  GuidePrinciplesGrid,
  GuideStrategyByGoalSection,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

import { BeyondCollegeSection } from './components';
import { useAgentContext } from './CourseSelectionGuidePageContainer.agentContext';
import { courseSelectionContent } from '../content';

const STRATEGY_ICONS: Record<string, AppIcons> = {
  'college-bound': 'GraduationHat02',
  'career-technical': 'Tool02',
  exploration: 'Compass03',
};

const SECTION_IDS = {
  overview: 'course-selection-section-overview',
  beyondCollege: 'course-selection-section-beyond-college',
  tools: 'course-selection-section-tools',
  keyPrinciples: 'course-selection-section-key-principles',
  myths: 'course-selection-section-myths',
  strategy: 'course-selection-section-strategy',
  faq: 'course-selection-section-faq',
} as const;

const COURSE_SELECTION_TOOL_ID = 'course_selection_guide';

const COURSE_SELECTION_TOOL_IDS: readonly StickerCardId[] = [
  'are_you_on_track',
  'majors_and_courses',
  'what_are_your_chances',
];

export const CourseSelectionGuidePageContainer = () => {
  useAgentContext();

  const sections = [
    {
      id: SECTION_IDS.tools,
      title: courseSelectionContent.tools.title,
      subtitle: courseSelectionContent.tools.subtitle,
      rightSlot: (
        <LubavButton
          id="course-selection-tools-lubav-btn"
          label="Which tool should I start with?"
          prompt="Which of these tools should I start with to support my course selection planning?"
          allowTools={false}
        />
      ),
      content: <GuideToolsSection toolIds={COURSE_SELECTION_TOOL_IDS} />,
    },
    {
      id: SECTION_IDS.beyondCollege,
      title: courseSelectionContent.beyondCollege.title,
      subtitle: courseSelectionContent.beyondCollege.subtitle,
      rightSlot: (
        <LubavButton
          id="course-selection-beyond-college-lubav-btn"
          label="How does this apply to me?"
          prompt="Based on my profile and goals, which of these broader benefits of thoughtful course selection matters most for me right now? Consider career exploration, skill building, scholarships, recommendations, confidence, and graduation readiness."
          allowTools={false}
        />
      ),
      content: <BeyondCollegeSection content={courseSelectionContent.beyondCollege} />,
    },
    {
      id: SECTION_IDS.keyPrinciples,
      title: courseSelectionContent.keyPrinciples.title,
      subtitle: courseSelectionContent.keyPrinciples.subtitle,
      rightSlot: (
        <LubavButton
          id="course-selection-principles-lubav-btn"
          label="Which matters most for me?"
          prompt="Based on what you know about me, which of the five key principles for course selection should I focus on most: (1) Balance Challenge and Capacity, (2) Align with Goals and Interests, (3) Show a Strong Core, (4) Think Long-Term, Plan Ahead, or (5) Consult and Reflect? Explain why that principle applies to my situation and give me one actionable step."
          allowTools={false}
        />
      ),
      content: <GuidePrinciplesGrid principles={courseSelectionContent.keyPrinciples.principles} />,
    },
    {
      id: SECTION_IDS.myths,
      title: courseSelectionContent.mythsAndMistakes.title,
      subtitle: courseSelectionContent.mythsAndMistakes.subtitle,
      rightSlot: (
        <LubavButton
          id="course-selection-myths-lubav-btn"
          label="Discuss with Lubav"
          prompt="I just read the Course Selection Myths & Mistakes section. Based on what you know about me, identify which myth or mistake I'm most at risk for and explain why. Then tell me what I should do differently."
          allowTools={false}
        />
      ),
      content: (
        <GuideMythsAndMistakesSection
          intro={courseSelectionContent.mythsAndMistakes.intro}
          mythLabel={courseSelectionContent.mythsAndMistakes.mythLabel}
          mistakeLabel={courseSelectionContent.mythsAndMistakes.mistakeLabel}
          items={courseSelectionContent.mythsAndMistakes.items}
        />
      ),
    },
    {
      id: SECTION_IDS.strategy,
      title: courseSelectionContent.strategyByGoal.title,
      subtitle: courseSelectionContent.strategyByGoal.subtitle,
      rightSlot: (
        <LubavButton
          id="course-selection-strategy-lubav-btn"
          label="Which pathway fits me?"
          prompt="Based on my profile, goals, and intended path, which course selection pathway fits me best: college-bound, career and technical, or exploration/personal growth? Give me a personalized version of the advice for my pathway, tailored to my specific situation."
        />
      ),
      content: (
        <GuideStrategyByGoalSection
          strategies={courseSelectionContent.strategyByGoal.strategies}
          iconMap={STRATEGY_ICONS}
          columns={3}
        />
      ),
    },
    {
      id: SECTION_IDS.faq,
      title: courseSelectionContent.faq.title,
      subtitle: courseSelectionContent.faq.subtitle,
      rightSlot: (
        <LubavButton
          id="course-selection-faq-lubav-btn"
          label="Ask a question"
          prompt="I'm on the Course Selection Guide FAQ page. Based on what you know about me and my academic situation, what's the most important question I should be asking about course selection right now? Answer it for me with specific advice."
          allowTools={false}
        />
      ),
      content: (
        <GuideFaqSection
          idPrefix="course-selection-faq"
          questions={courseSelectionContent.faq.questions}
        />
      ),
    },
  ];

  return (
    <GuidePageShell
      toolId={COURSE_SELECTION_TOOL_ID}
      banner={{
        src: courseSelectionBanner,
        alt: 'The Ultimate Guide to High School Course Selection',
      }}
    >
      <GuideSections
        header={courseSelectionContent.header}
        overview={{
          id: SECTION_IDS.overview,
        }}
        sections={sections}
      />
    </GuidePageShell>
  );
};
